import Icon, { IconType } from 'components/Icon';
import React, { useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { selectIsMobile } from 'redux/mode/reducers';
import { RootReducer } from 'redux/reducers';
import { Tooltip } from 'components/Widget/Tooltip';
import * as S from './styles';

interface MarkdownTextProps {
	children: string;
	onContextMenu?: (e: React.MouseEvent) => void;
}

const CodeBlock = React.memo(({ children }: { children: string }) => {
	const [isCollapsed, setIsCollapsed] = useState(true);
	const [tooltipId] = useState(String(Math.random()));
	const lineCount = (children?.match(/\n/g) || []).length + 1;
	const hasOverflow = lineCount > 5;

	const handleCopy = async () => {
		try {
			await navigator.clipboard.writeText(children);
			toast.success('Copied to clipboard');
		} catch (error) {
			toast.error('Failed to copy code');
		}
	};

	return (
		<S.CodeBlockWrapper $isCollapsed={isCollapsed} $hasOverflow={hasOverflow}>
			<Tooltip placement="bottom" id={tooltipId} />
			<S.CodeActionsTop>
				<S.IconButton
					onClick={handleCopy}
					data-tooltip-id={tooltipId}
					data-tooltip-content="Copy to clipboard"
				>
					<Icon type={IconType.COPY} width={14} height={14} color="currentColor" />
				</S.IconButton>
			</S.CodeActionsTop>
			<pre>
				<code>{children}</code>
			</pre>
			{hasOverflow && (
				<S.ShowMoreLink onClick={() => setIsCollapsed(!isCollapsed)}>
					{isCollapsed ? 'Show more' : 'Show less'}
				</S.ShowMoreLink>
			)}
		</S.CodeBlockWrapper>
	);
});

CodeBlock.displayName = 'CodeBlock';

const MarkdownText = ({ children, onContextMenu }: MarkdownTextProps) => {
	const isMobile = useSelector((r: RootReducer) => selectIsMobile(r.mode));

	// Convert bullet point characters to markdown format
	// Convert • to - for proper markdown bullet lists
	const processedContent = children.replace(/^• /gm, '- ');

	// Memoize components to prevent CodeBlock from being recreated on every render
	const components = useMemo(
		() => ({
			code: ({ inline, children, ...props }: any) => {
				const codeString = String(children);
				// Only use CodeBlock for multi-line code blocks
				// Single-line code (inline or block) should render as simple inline code
				const isMultiLine = codeString.includes('\n');
				if (inline || !isMultiLine) {
					return <code {...props}>{children}</code>;
				}
				return <CodeBlock>{codeString}</CodeBlock>;
			},
		}),
		[]
	);

	return (
		<S.MarkdownContent onContextMenu={onContextMenu} isMobile={isMobile}>
			<ReactMarkdown components={components}>{processedContent}</ReactMarkdown>
		</S.MarkdownContent>
	);
};

export default MarkdownText;
